import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { MapPin, Train, Landmark, Building2, Phone } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import type { BranchMeta } from "@/content/locations/meta.gen";
import {
  KIND_LABEL_TH,
  REGION_LABEL_TH,
  MEETING_POINT_KIND_LABEL_TH,
} from "@/content/locations/labels";
import type { BranchDetail } from "@/content/locations/branch-detail";
import type { BranchLocation } from "@/content/locations/types";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

export const Route = createFileRoute("/locations/$branch")({
  loader: async ({
    params,
  }): Promise<BranchDetail & { nearby: BranchMeta[] }> => {
    const [{ loadBranchDetail }, { BRANCH_META }] = await Promise.all([
      import("@/content/locations/branch-detail"),
      import("@/content/locations/meta.gen"),
    ]);
    const detail = loadBranchDetail(params.branch);
    if (!detail) throw notFound();
    const nearby = BRANCH_META.filter(
      (x) => x.region === detail.branch.region && x.slug !== detail.branch.slug,
    ).slice(0, 6);
    return { ...detail, nearby };
  },
  head: ({ params, loaderData }) => {
    const b = loaderData?.branch;
    if (!b) {
      return pageHeadForProvince({
        title: "ไม่พบจุดให้บริการ",
        description: "ไม่พบจุดให้บริการนี้",
        path: `/locations/${params.branch}`,
      });
    }
    const site = getActiveSiteUrl();
    const office = loaderData?.office ?? false;
    const nameTh = b.nameTh;
    const nameEn = b.nameEn;
    const provinceTh = b.provinceTh;
    const slug = b.slug;
    const rawTitle = `${nameTh} — Notary & แปลรับรอง`;
    const title = rawTitle.length > 60 ? rawTitle.slice(0, 59) + "…" : rawTitle;
    const near = b.landmarks.slice(0, 2).join(", ");
    const desc = `${office ? "สำนักงาน" : "พื้นที่ให้บริการ"} ${nameTh} (${nameEn}) ${provinceTh} — Notary Public, แปลรับรอง, นิติกรณ์กงสุล/สถานทูต${near ? ` ใกล้ ${near}` : ""} โทร ${COMPANY.phone}`.slice(0, 158);
    const base = pageHeadForProvince({ title, description: desc, path: `/locations/${slug}` });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "จุดให้บริการ", url: `${site}/locations` },
              { name: nameTh, url: `${site}/locations/${slug}` },
            ]),
          ),
        },
        ...(loaderData
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(loaderData.branchSchema),
              },
              {
                type: "application/ld+json",
                children: JSON.stringify(buildFaqSchema(loaderData.faqs)),
              },
            ]
          : []),
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/locations/${params.branch}`)) },
      ],
    };
  },
  component: BranchPage,
});

function BranchPage() {
  const { branch: b, office, meetingPoints: mps, faqs, serviceLabels, nearby } =
    Route.useLoaderData() as BranchDetail & { nearby: BranchMeta[] };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-12">
        <nav className="text-sm text-muted-foreground">
          <Link to="/locations" className="hover:text-primary">
            จุดให้บริการ
          </Link>
          <span className="mx-2">/</span>
          {REGION_LABEL_TH[b.region] ?? b.region}
        </nav>

        <h1 className="mt-3 text-3xl font-bold md:text-4xl">{b.nameTh}</h1>
        <p className="mt-1 text-muted-foreground">{b.nameEn}</p>

        <div className="mt-6 rounded-lg border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-sm font-medium">
            {office ? <Building2 className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
            {office ? "สำนักงานที่มีเจ้าหน้าที่ประจำ" : "พื้นที่ให้บริการ / จุดนัดพบ (ไม่ใช่หน้าร้าน)"}
            <span className="text-muted-foreground">· {KIND_LABEL_TH[b.kind] ?? b.kind}</span>
          </div>
          <p className="mt-3 text-sm">{b.addressTh}</p>
          {b.mapUrl && (
            <p className="mt-2 text-sm">
              <a
                href={b.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:underline"
              >
                <MapPin className="h-4 w-4" /> เปิดแผนที่ Google Maps
              </a>
            </p>
          )}
          <p className="mt-3 flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4" /> {COMPANY.phone} · LINE {COMPANY.lineId} · {COMPANY.hours}
          </p>
        </div>

        {b.landmarks.length > 0 && (
          <section className="mt-8">
            <h2 className="flex items-center gap-2 text-xl font-semibold">
              <Landmark className="h-5 w-5" /> จุดสังเกตใกล้เคียง
            </h2>
            <ul className="mt-3 list-inside list-disc text-sm text-muted-foreground">
              {b.landmarks.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </section>
        )}

        {b.transit.length > 0 && (
          <section className="mt-8">
            <h2 className="flex items-center gap-2 text-xl font-semibold">
              <Train className="h-5 w-5" /> การเดินทาง
            </h2>
            <ul className="mt-3 list-inside list-disc text-sm text-muted-foreground">
              {b.transit.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-8">
          <h2 className="text-xl font-semibold">บริการเด่นของจุดนี้</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {serviceLabels.map((s) => (
              <Link
                key={s.slug}
                to="/services/$service"
                params={{ service: s.slug }}
                className="rounded-full border border-border px-3 py-1 text-sm hover:border-primary"
              >
                {s.label}
              </Link>
            ))}
          </div>
        </section>

        {mps.length > 0 && (
          <section className="mt-8">
            <h2 className="text-xl font-semibold">จุดนัดพบที่รองรับจากจุดนี้</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              จุดนัดพบคือสถานที่สาธารณะที่ทีมเดินทางไปพบลูกค้า ไม่ใช่สำนักงานของเรา
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {mps.map((m) => (
                <span
                  key={m.slug}
                  className="rounded-md bg-muted px-2.5 py-1 text-xs text-muted-foreground"
                >
                  {MEETING_POINT_KIND_LABEL_TH[m.kind]} · {m.nameTh}
                </span>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <h2 className="text-xl font-semibold">คำถามที่พบบ่อยเกี่ยวกับจุดนี้</h2>
          <div className="mt-4 space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-lg border border-border p-4">
                <h3 className="font-medium">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {nearby.length > 0 && (
          <section className="mt-10">
            <h2 className="text-xl font-semibold">จุดให้บริการใกล้เคียง</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to="/locations/$branch"
                  params={{ branch: n.slug }}
                  className="rounded-full border border-border px-3 py-1 text-sm hover:border-primary"
                >
                  {n.nameTh}
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
